import type { ReactElement } from 'react'
import { isDefined, type LocationExchangeUnit, type LocationId } from '@terros/common'
import { Select } from '@mantine/core'
import { useTranslation } from '@front/model'

type Props = {
  units: LocationExchangeUnit[]
  locationId?: LocationId
  required: boolean
  onUnitChange: (unit: LocationExchangeUnit) => void
}

/** The units we know of at the searched address: address search doesn't return units */
export const UnitSelect = ({ units, locationId, required, onUnitChange }: Props): ReactElement => {
  const { t } = useTranslation()
  const selected = units.find((unit) => unit.details.locationId === locationId)

  const handleChange = (value: string | null): void => {
    const unit = units.find((u) => u.details.locationId === value)
    if (unit) onUnitChange(unit)
  }

  return (
    <Select
      data={units.flatMap(toOption)}
      label={t('Unit')}
      placeholder={t('Choose a unit')}
      required={required}
      value={selected?.details.locationId ?? null}
      searchable
      onChange={handleChange}
    />
  )
}

type UnitOption = {
  value: LocationId
  label: string
}

/** keyed by location id: two units can share a label */
function toOption(unit: LocationExchangeUnit): UnitOption[] {
  const { locationId } = unit.details
  return isDefined(locationId) ? [{ value: locationId, label: unit.label }] : []
}
